import styled, { css } from '@emotion/native';
import React from 'react';
import {
  NativeSyntheticEvent,
  Platform,
  PressableProps,
  TargetedEvent,
  useColorScheme,
} from 'react-native';

import { fontSizes, fontWeights, getBorderRadius } from '../constants';
import { colors } from '../constants/colors';
import { getSpacing } from '../constants/spacing';
import { getRecordValue, isWeb } from '../utils';

type ButtonVariant = 'primary' | 'secondary' | 'tertiary';

interface StyledProps extends ButtonProps {
  isHovered: boolean;
  isActive: boolean;
  isFocused: boolean;
  fullWidth?: boolean;
  width?: string;
}

const baseStyles = `
  display: flex;
  align-items: center;
  justify-content: center;
  padding-horizontal: ${getSpacing('xs')};
  padding-vertical: ${getSpacing('xs')};
  min-height: ${getSpacing('xl')};
  border-radius: ${getBorderRadius('m')};
  border: 1px solid transparent;
  font-size: 16px;
  transition: all 0.3s ease;
  cursor: pointer;
  outline-color: ${colors.black};
  outline-offset: 2px;
`;

const disabledStyle = `
  background-color: ${colors.gray3};
  cursor: not-allowed;
`;

const backgroundColorMap: Record<
  ButtonVariant,
  Record<'dark' | 'light', Record<'active' | 'inactive', string>>
> = {
  primary: {
    light: { active: colors.primaryHover, inactive: colors.primary },
    dark: { active: colors.primaryHover, inactive: colors.primary },
  },
  secondary: {
    light: { active: colors.blackHover, inactive: colors.black },
    dark: { active: colors.gray4, inactive: colors.white },
  },
  tertiary: {
    light: { active: colors.gray4, inactive: colors.white },
    dark: { active: 'transparent', inactive: 'transparent' },
  },
};

const getBackgroundColor = (
  variant: ButtonVariant = 'primary',
  hovered = false,
  active = false,
  dark = false
): string => {
  const theme = dark ? 'dark' : 'light';
  const state = hovered || active ? 'active' : 'inactive';
  const themeMap = getRecordValue(backgroundColorMap, variant);
  const stateMap = themeMap && getRecordValue(themeMap, theme);
  return (stateMap && getRecordValue(stateMap, state)) ?? colors.primary;
};

const getBorder = (
  variant: ButtonVariant = 'primary',
  active = false,
  dark = false
) => {
  if (variant === 'tertiary' && dark) {
    return `1px solid ${colors.white}`;
  }
  if (variant === 'tertiary' && !active) {
    return `1px solid ${colors.gray2}`;
  }
  return '1px solid transparent';
};

const StyledButton = styled.Pressable<StyledProps>`
  ${baseStyles};
  background-color: ${(props) =>
    getBackgroundColor(
      props.variant,
      props.isHovered,
      props.isActive,
      props.dark
    )};
  border: ${(props) => getBorder(props.variant, props.isActive, props.dark)};
  ${(props) =>
    props.disabled &&
    css`
      ${baseStyles};
      ${disabledStyle};
    `};
  width: ${(props) => (props.fullWidth ? '100%' : props.width || 'auto')};
  min-height: 48px;
`;

const textColorMap: Record<ButtonVariant, Record<'dark' | 'light', string>> = {
  primary: { light: colors.black, dark: colors.black },
  secondary: { light: colors.white, dark: colors.black },
  tertiary: { light: colors.black, dark: colors.white },
};

const getButtonTextColor = (
  variant: ButtonVariant = 'primary',
  disabled = false,
  dark = false
): string => {
  if (disabled) return colors.gray1;
  const theme = dark ? 'dark' : 'light';
  const themeMap = getRecordValue(textColorMap, variant);
  return (themeMap && getRecordValue(themeMap, theme)) ?? colors.black;
};

const StyledText = styled.Text<StyledProps>`
  font-family: ${isWeb() ? 'inherit' : 'PlusJakartaSans-ExtraBold'};
  ${isWeb() ? `font-weight: ${fontWeights.extraBold};` : ''}
  font-size: ${`${fontSizes.m}`}px;
  color: ${(props) =>
    getButtonTextColor(props.variant, props.disabled, props.dark)};
  text-align: center;
`;

const StyledWrapper = styled.View`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

export interface ButtonProps extends PressableProps {
  variant?: ButtonVariant;
  disabled?: boolean;
  label?: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  type?: 'button' | 'submit';
  dark?: boolean;
  fullWidth?: boolean;
  width?: string;
}

export const Button: React.FC<
  React.PropsWithChildren & PressableProps & ButtonProps
> = ({
  children,
  variant = 'primary',
  disabled = false,
  label,
  iconRight,
  iconLeft,
  type = 'submit',
  fullWidth = false,
  dark,
  width = 'auto',
  ...props
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isActive, setIsActive] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const colorSchemeState = useColorScheme();

  const colorScheme = Platform.select({
    web: 'light',
    default: colorSchemeState,
  });

  const handleFocus = (e: NativeSyntheticEvent<TargetedEvent>) => {
    setIsFocused(true);
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const handleBlur = (e: NativeSyntheticEvent<TargetedEvent>) => {
    setIsFocused(false);
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  return (
    <StyledButton
      role="button"
      variant={variant}
      dark={dark || colorScheme === 'dark'}
      disabled={disabled}
      isHovered={isHovered}
      isActive={isActive}
      isFocused={isFocused}
      fullWidth={fullWidth}
      width={width}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPressIn={() => setIsActive(true)}
      onPressOut={() => setIsActive(false)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...props}
    >
      <StyledWrapper>
        {iconLeft}
        {label && (
          <StyledText
            variant={variant}
            dark={dark || colorScheme === 'dark'}
            disabled={disabled}
            isFocused={isFocused}
            isHovered={isHovered}
            isActive={isActive}
          >
            {label}
          </StyledText>
        )}
        {iconRight}
      </StyledWrapper>
    </StyledButton>
  );
};
